// Ported from design/project/App35-Offline.dc.html and App68-Error.dc.html: the connection dropped, or a screen
// failed. Both say what's safe; the error's reference is sent to Dev and System so a player's report matches.
import { Component, useEffect, useRef, type ErrorInfo, type ReactNode } from "react";
import { api } from "../api.js";
import { useGame } from "../game.js";
import { SUPPORT_URL } from "../money.js";
import { tg } from "../telegram.js";

const openTelegram = (url: string) => (tg?.openTelegramLink ? tg.openTelegramLink(url) : void window.open(url, "_blank"));

/** Over whatever screen is open: the connection dropped. Clears itself when it's back. */
export function ConnectionLost() {
  const { connection, battle, reconnect } = useGame();
  const live = battle?.state === "live" || battle?.state === "settling";
  return (
    <div role="alertdialog" aria-modal="true" aria-label="Connection lost" style={{ position: "fixed", inset: "0", zIndex: 40, background: "rgba(11,14,20,0.82)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <style>{`@keyframes ddSpin{to{transform:rotate(360deg)}}`}</style>
      <div className="g" style={{ width: "100%", maxWidth: "380px", boxSizing: "border-box", borderRadius: "26px", padding: "24px 22px", display: "flex", flexDirection: "column", alignItems: "center", background: "rgba(18,22,29,0.97)" }}>
        <div style={{ width: "76px", height: "76px", borderRadius: "22px", background: "rgba(255,51,102,0.12)", border: "1px solid rgba(255,51,102,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#FF3366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 8.5a15 15 0 0 1 20 0M5 12a10 10 0 0 1 14 0M8.5 15.5a5 5 0 0 1 7 0"></path><circle cx="12" cy="19" r="1"></circle><path d="M3 3l18 18"></path></svg>
        </div>
        <span className="h2" style={{ marginTop: "16px", textAlign: "center" }}>Connection lost</span>
        <p className="muted" style={{ margin: "8px 0 0", textAlign: "center", fontSize: "14px", lineHeight: "21px" }}>
          {live ? "Your battle is still running. Your stake is safe in escrow and the result settles without you." : "Your balance is safe. We'll pick up where you left off as soon as you're back online."}
        </p>
        <div style={{ marginTop: "16px", width: "100%", boxSizing: "border-box", padding: "12px", borderRadius: "14px", background: "rgba(255,255,255,0.04)", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", fontSize: "13px" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: "ddSpin 1s linear infinite" }} aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="#30363D" strokeWidth="3"></circle><path d="M12 3 A9 9 0 0 1 21 12" fill="none" stroke="#CCFF00" strokeWidth="3" strokeLinecap="round"></path></svg>
          <span style={{ fontWeight: "600" }}>Reconnecting</span>{connection.attempt > 1 && <span className="mono muted">(attempt {connection.attempt})</span>}
        </div>
        <button className="btnP" style={{ marginTop: "16px", width: "100%" }} onClick={reconnect}>TRY AGAIN NOW</button>
      </div>
    </div>
  );
}

/** A screen that couldn't load or crashed. `status` is the server's answer, 0 when the screen itself failed. */
export function ErrorScreen({ status, refCode, onRetry }: { status: number; refCode: string; onRetry: () => void }) {
  return (
    <div className="screen">
      <div className="orbR" aria-hidden="true" style={{ top: "40px", right: "-200px", width: "460px", height: "460px" }}></div>
      <div className="pad squeeze" style={{ height: "100%", paddingBottom: "calc(var(--dd-safe-bottom, 0px) + 20px)" }}>
        <div style={{ marginTop: "auto", display: "flex", justifyContent: "center" }}>
          <div style={{ width: "88px", height: "88px", borderRadius: "28px", background: "rgba(255,51,102,0.1)", border: "1px solid rgba(255,51,102,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#FF3366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3 2 20h20L12 3z"></path><path d="M12 10v4M12 17v.5"></path></svg>
          </div>
        </div>
        <h1 className="h1" style={{ marginTop: "22px", textAlign: "center" }}>Something went wrong</h1>
        <p className="muted" style={{ margin: "8px 6px 0", textAlign: "center", fontSize: "14px", lineHeight: "21px" }}>We couldn't load this screen. Your balance and any live battle are safe, they don't depend on this page.</p>
        <span className="chip chipN mono" style={{ marginTop: "16px", alignSelf: "center" }}>{status ? `ERR ${status} · ` : ""}REF {refCode}</span>
        <div style={{ marginTop: "auto", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
          <button className="btnP" onClick={onRetry}>TRY AGAIN</button>
          <button className="btnS" onClick={() => openTelegram(SUPPORT_URL)}>Contact support</button>
        </div>
      </div>
    </div>
  );
}

/** The server keeps erroring on load: shown until a reload works. */
export function ServerError() {
  const { connection, reconnect } = useGame();
  const sent = useRef(false);
  useEffect(() => {
    if (!connection.failed || sent.current) return;
    sent.current = true;
    void api("/v1/client-error", { method: "POST", body: { ref: connection.failed.ref, screen: "load", message: `server answered ${connection.failed.status}` } }).catch(() => {});
  }, [connection.failed]);
  return <ErrorScreen status={connection.failed?.status ?? 0} refCode={connection.failed?.ref ?? "------"} onRetry={reconnect} />;
}

/** Catches a crash in a screen and shows the error screen instead of a blank app. */
export class ScreenBoundary extends Component<{ route: string; children: ReactNode }, { ref: string | null }> {
  state = { ref: null as string | null };
  static getDerivedStateFromError() {
    return { ref: Math.random().toString(16).slice(2, 8) };
  }
  componentDidCatch(err: Error, info: ErrorInfo) {
    void api("/v1/client-error", { method: "POST", body: { ref: this.state.ref, screen: this.props.route, message: `${err.message} ${info.componentStack?.split("\n")[1]?.trim() ?? ""}` } }).catch(() => {});
  }
  componentDidUpdate(prev: { route: string }) {
    // Moving to another screen starts fresh.
    if (prev.route !== this.props.route && this.state.ref) this.setState({ ref: null });
  }
  render() {
    if (this.state.ref) return <ErrorScreen status={0} refCode={this.state.ref} onRetry={() => this.setState({ ref: null })} />;
    return this.props.children;
  }
}
